// src/app/pages/dashboard/profile/home.page.ts
import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { BadgeModule } from 'primeng/badge';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { SkeletonModule } from 'primeng/skeleton';
import { TabViewModule } from 'primeng/tabview';
import { ProgressBarModule } from 'primeng/progressbar';
import { AvatarModule } from 'primeng/avatar';
import { Menu, MenuModule } from 'primeng/menu';
import { MenuItem, MessageService } from 'primeng/api';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { ToastService } from 'src/app/services/toast.service';
import { Auth } from '@angular/fire/auth';
import { FeedPost } from 'src/app/interfaces/post';
import { OrganizationSearchService } from '../../../services/organization-search.service';
import { FollowerCountPipe } from '../../../pipes/follower-count.pipe';
import { UserTypeBadgeComponent } from '../../../shared/components/user-type-badge/user-type-badge.component';
import { ToolbarModule } from 'primeng/toolbar';
import { FeedService } from 'src/app/services/feed.service';
import { CommentsComponent } from '../hub/comments/comments.component';
import { LikeService } from 'src/app/services/like.service';
import { ReportService } from 'src/app/services/report.service';
import { ProfileDataService } from 'src/app/services/profile-data.service';
import { InvoiceService, Invoice } from '../../../services/invoice.service';
import {
  DocumentScanner,
  ResponseType,
  ScanDocumentResponseStatus,
} from '@capgo/capacitor-document-scanner';
import { Capacitor } from '@capacitor/core';
import { Filesystem } from '@capacitor/filesystem';
import { DialogModule } from 'primeng/dialog';
import { NavController, Platform } from '@ionic/angular';
import { TransactionService, FormattedTransaction } from 'src/app/services/transaction.service';
import { OrdersListComponent } from './orders-list/orders-list.component';
import { TicketsListComponent } from './tickets-list/tickets-list.component';
import { ShareSheetComponent } from '../../shared/share-sheet/share-sheet.component';
import { FeedCardComponent } from '../../shared/feed-card/feed-card.component';
import { EnergyStateService } from 'src/app/services/energy-state.service';
import { TransactionsSheetComponent } from './transactions-sheet/transactions-sheet.component';
import { DonationSheetComponent } from 'src/app/shared/donation-sheet/donation-sheet.component';
import { DonationService, DonationStats } from 'src/app/services/donation-service';
import { BadgeService, BadgeWithProgress } from 'src/app/services/badge.service';
import { BadgesSheetComponent } from '../../shared/badges-sheet/badges-sheet.component';
import { IonModal } from '@ionic/angular/standalone';
import { PollResultSheetComponent } from '../../shared/poll-result-sheet/poll-result-sheet.component';

interface ProfileData {
  user_id: string;
  first_name?: string;
  last_name?: string;
  energy_connection_status?: string;
  avatar?: string;
  is_energy_customer?: boolean;
  date_of_birth?: string;
  regional_hub_id?: number;
  city: string;
  regional_hub_name?: string;
  created_at?: string;
  user_active_status?: boolean;
  interests?: string[];
  smart_coins?: number;
  impact_coins?: number;
  good_will_score?: number;
  level?: number;
  user_role?: string;
  follower_count: number;
  following_count: number;
  post_count: number;
  display_name?: string;
  type?: 'business' | 'regional' | 'experience';
  verified?: boolean;
}

interface Challenge {
  id: string;
  title: string;
  description: string;
  image: string;
  points: number;
  participants: number;
  endDate: Date;
  category: 'sustainability' | 'community' | 'health' | 'education';
  isParticipating: boolean;
  progress?: number;
  current?: number;
  target?: number;
}

interface CoinSource {
  category: string;
  businessHubId: number;
  amount: number;
  percentage: number;
  transactionCount: number;
  color: string;
  icon: string;
}

@Component({
  selector: 'app-profile',
  templateUrl: './home.page.html',
  styleUrls: ['./home.page.scss'],
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    ButtonModule,
    TagModule,
    BadgeModule,
    BreadcrumbModule,
    SkeletonModule,
    TabViewModule,
    ProgressBarModule,
    AvatarModule,
    RouterModule,
    MenuModule,
    FollowerCountPipe,
    UserTypeBadgeComponent,
    ToolbarModule,
    CommentsComponent,
    DialogModule,
    OrdersListComponent,
    TicketsListComponent,
    FeedCardComponent,
    ShareSheetComponent,
    TransactionsSheetComponent,
    DonationSheetComponent,
    BadgesSheetComponent,
    IonModal,
    PollResultSheetComponent,
    TooltipModule
  ],
  providers: [MessageService]
})
export class HomePage implements OnInit {

  goBack() {
    this._nav.back();
  }

  openInvoice(invoice: any) {
    window.open(invoice.downloadUrl, '_blank');
  }

  postsPage = 1;
  postsPageSize = 20;
  @ViewChild('postMenu') postMenu!: Menu;

  profile: ProfileData | null = null;
  posts: FeedPost[] = [];
  isLoadingProfile = true;
  isLoadingPosts = true;
  isFollowing = false;
  isFollowLoading = false;
  notificationEnabled = false;
  isNotificationLoading = false;

  pollResultsSheetVisible = false;
  selectedPollPost: FeedPost | null = null;
  isPollCreator = false;
  // Energy Management Banner
  get showEnergyBanner(): boolean {
    ;

    return !!this.profile?.is_energy_customer && this.profile?.energy_connection_status == 'connected';
  }
  energyData: string | null = null;
  index = 0;
  stopPostsPagination = false;

  // Smart Coins Data
  totalCoins: number = 0;
  monthlyEarned: number = 0;
  monthlySpent: number = 0;
  coinTransactions: FormattedTransaction[] = [];
  coinSources: CoinSource[] = [];
  currentUserId: string = '';

  // Transactions Sheet
  transactionsSheetVisible = false;

  // Donations
  donationStats: DonationStats | null = null;
  donationSheetVisible = false;

  // Challenges Data
  activeChallenges: Challenge[] = [];

  // Scanner Data
  scannerDialogVisible = false;
  scannedImage?: string;
  scannedImageDataUrl?: string;
  isAnalysisInProgress = false;
  processingStatus = '';
  processedInvoice?: Invoice;
  errorMessage = '';

  // Menu
  selectedPost: FeedPost | null = null;
  commentsSheetVisible = false;
  selectedPostForMenu: FeedPost | null = null;
  postMenuItems: MenuItem[] = [
    {
      label: 'Melden',
      icon: 'pi pi-flag',
      command: () => {
        if (this.selectedPostForMenu) {
          this.reportPost(this.selectedPostForMenu);
        }
      }
    }
  ];

  // Share Sheet
  shareSheetVisible = false;
  postToShare: FeedPost | null = null;

  home: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard' };
  items: MenuItem[] = [
    { label: 'Feed', routerLink: '/dashboard/feed' },
    { label: 'Profile' }
  ];

  // Badges
  badges: any[] = [];
  topBadges: BadgeWithProgress[] = [];
  achievedBadges: BadgeWithProgress[] = [];
  inProgressBadges: BadgeWithProgress[] = [];
  badgesSheetVisible = false;
  isLoadingBadges = false;

  constructor(
    private _nav: NavController,
    private _http: HttpClient,
    private _toast: ToastService,
    private _auth: Auth,
    private _organizationService: OrganizationSearchService,
    private _messageService: MessageService,
    private _feedService: FeedService,
    private _reportService: ReportService,
    private _profileService: ProfileDataService,
    private _invoiceService: InvoiceService,
    private platform: Platform,
    private _transactionService: TransactionService,
    private activatedRoute: ActivatedRoute,
    private _energyStateService: EnergyStateService,
    private _donationService: DonationService,
    private _badgeService: BadgeService
  ) { }

  isViewMode: boolean = false;

  ngOnInit(): void {
    if (this.activatedRoute.snapshot.paramMap.get('userId')) {
      this.currentUserId = this.activatedRoute.snapshot.paramMap.get('userId')!;
      this.isViewMode = true;
    } else {
      this._profileService.getUserProfileObservable().subscribe(profile => {
        this.currentUserId = profile.user_id || '';
      });
    }

    this.loadProfile();
    this.loadPosts();
    this.loadBadges();

    if (!this.isViewMode) {
      this.loadSmartCoinsData();
      this.loadDonationStats();
      this.loadEnergyData();
    }
  }

  async loadProfile() {
    this.isLoadingProfile = true;

    try {
      let response;

      if (this.isViewMode && this.currentUserId) {
        const profileResponse = await firstValueFrom(
          this._http.get<any>(`${environment.apiUrl}/experience/search/profile/experience/${this.currentUserId}`)
        );
        if (profileResponse?.status === 'success') {
          response = profileResponse.data;
        }
      } else {
        response = await firstValueFrom(
          this._http.get<any>(`${environment.apiUrl}/experience/users/details`)
        );
      }

      if (response) {
        response.display_name = `${response.first_name || ''} ${response.last_name || ''}`.trim();
        response.type = 'experience';
        this.totalCoins = response.smart_coins || 0;
        this.profile = response;

        await this.checkFollowStatus();
      }
    } catch (error) {
      this._toast.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'Das Profil konnte nicht geladen werden.'
      });
    }

    this.isLoadingProfile = false;
  }

  getAge(): number | null {
    if (!this.profile?.date_of_birth) return null;

    const birthDate = new Date(this.profile.date_of_birth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  }

  getAccountAge(): string {
    if (!this.profile?.created_at) return '';

    const createdDate = new Date(this.profile.created_at);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - createdDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 30) {
      return `${diffDays} Tage`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} ${months === 1 ? 'Monat' : 'Monate'}`;
    } else {
      const years = Math.floor(diffDays / 365);
      return `${years} ${years === 1 ? 'Jahr' : 'Jahre'}`;
    }
  }

  async loadPosts(loadMore: boolean = false) {
    if (this.stopPostsPagination && loadMore) return;

    if (loadMore) {
      this.isLoadingPosts = true;
    }

    try {
      if (!loadMore) {
        this.postsPage = 1;
        this.stopPostsPagination = false;
      }

      let newPosts;

      if (this.isViewMode && this.currentUserId) {
        const response = await firstValueFrom(
          this._http.get<any>(`${environment.apiUrl}/experience/search/profile/experience/${this.currentUserId}/posts`, {
            params: {
              page: this.postsPage.toString(),
              size: this.postsPageSize.toString()
            }
          })
        );
        newPosts = response || [];
      } else {
        newPosts = await this._feedService.getPosts({
          page: this.postsPage,
          size: this.postsPageSize,
          tagged_organization_type: 'experience'
        });
      }

      const processedPosts = newPosts.map((post: any) => ({
        ...post,
        likes: Array.isArray(post.likes) ? post.likes : []
      }));

      if (processedPosts.length === 0) {
        this.stopPostsPagination = true;
      } else {
        if (loadMore) {
          this.posts = [...this.posts, ...processedPosts];
        } else {
          this.posts = processedPosts;
        }
        this.postsPage++;
      }

    } catch (error) {
      console.error('Error loading posts:', error);
      this._messageService.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'Posts konnten nicht geladen werden.'
      });
    }

    this.isLoadingPosts = false;
  }

  async loadSmartCoinsData() {
    try {
      const stats = await firstValueFrom(this._transactionService.getStats());
      this.totalCoins = stats.totalBalance;
      this.monthlyEarned = stats.monthlyEarned;
      this.monthlySpent = stats.monthlySpent;
      this.coinSources = stats.topSources;

      const transactionsResponse = await firstValueFrom(
        this._transactionService.getTransactions(6, 0)
      );
      this.coinTransactions = transactionsResponse.transactions.map(t =>
        this._transactionService.formatTransaction(t)
      );

    } catch (error) {
      console.error('Error loading smart coins data:', error);
      this._toast.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'Smart Coins Daten konnten nicht geladen werden.'
      });
    }
  }

  async loadDonationStats() {
    try {
      const response = await firstValueFrom(this._donationService.getRegionalStats());
      if (response.success) {
        this.donationStats = response.stats;
      }
    } catch (error) {
      console.error('Error loading donation stats:', error);
    }
  }

  async loadEnergyData() {


    try {
      this._energyStateService.latestMeterReading$.subscribe((reading) => {
        if (reading?.value) {
          const consumption = parseFloat(reading.value);
          this.energyData = `${consumption.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kWh`;
        }
      });




      if (this.profile?.is_energy_customer) {
        await this._energyStateService.loadEnergyData();
      }
    } catch (error) {
      console.error('Error loading energy data:', error);
    }
  }

  async loadBadges() {
    this.isLoadingBadges = true;

    const response = await firstValueFrom(this._badgeService.getMyBadges());
    console.log(response);

    if (response.badges) {
      this.badges = response.badges;

      // Separate achieved and in-progress badges
      this.achievedBadges = this.badges.filter(b => b.isAchieved);
      this.inProgressBadges = this.badges.filter(b => !b.isAchieved);

      // Top 5 in-progress badges für die Home Page (sortiert nach Fortschritt)
      this.topBadges = this.inProgressBadges
        .sort((a, b) => b.progressPercentage - a.progressPercentage)
        .slice(0, 5);
    }

    this.isLoadingBadges = false;
  }

  async checkFollowStatus() {
    if (!this.profile) return;

    try {
      const response = await firstValueFrom(this._organizationService.getFollowing(1, 100));

      if (response?.status === 'success') {
        const profileUserId = String(this.profile.user_id);
        const follow = response.data.find(f =>
          f.organization && String(f.organization.user_id) === profileUserId
        );

        this.isFollowing = !!follow;

        if (follow?.organization) {
          this.notificationEnabled = follow.organization.notification_enabled || false;
        }
      }
    } catch (error) {
      console.error('Error checking follow status:', error);
    }
  }

  async toggleFollow() {
    if (!this.profile || this.isFollowLoading) return;

    const currentUser = this._auth.currentUser;
    if (currentUser?.uid === this.profile.user_id) return;

    this.isFollowLoading = true;
    
    // Store previous state for rollback on error
    const previousFollowState = this.isFollowing;
    const previousNotificationState = this.notificationEnabled;
    const previousFollowerCount = this.profile.follower_count;

    try {
      // Optimistic update: Update UI immediately
      if (this.isFollowing) {
        this.isFollowing = false;
        this.notificationEnabled = false;
        this.profile.follower_count--;
        
        this._toast.add({
          severity: 'success',
          summary: 'Erfolgreich',
          detail: `${this.profile.display_name} wurde entfolgt`
        });
        
        // Make HTTP request in background
        const response = await firstValueFrom(
          this._organizationService.unfollowOrganization(this.profile.user_id, this.profile.type!)
        );

        if (response?.status !== 'success') {
          // Rollback on failure
          this.isFollowing = previousFollowState;
          this.notificationEnabled = previousNotificationState;
          this.profile.follower_count = previousFollowerCount;
          throw new Error('Failed to unfollow');
        }
      } else {
        this.isFollowing = true;
        this.notificationEnabled = true;
        this.profile.follower_count++;
        
        this._toast.add({
          severity: 'success',
          summary: 'Erfolgreich',
          detail: `${this.profile.display_name} wird jetzt gefolgt`
        });
        
        // Make HTTP request in background
        const response = await firstValueFrom(
          this._organizationService.followOrganization(this.profile.user_id, this.profile.type!)
        );

        if (response?.status !== 'success') {
          // Rollback on failure
          this.isFollowing = previousFollowState;
          this.notificationEnabled = previousNotificationState;
          this.profile.follower_count = previousFollowerCount;
          throw new Error('Failed to follow');
        }
      }
    } catch (error) {
      this._toast.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'Fehler beim Aktualisieren des Follow-Status'
      });
      console.error('Follow toggle error:', error);
    }

    this.isFollowLoading = false;
  }

  async toggleNotifications() {
    if (!this.profile || !this.isFollowing || this.isNotificationLoading) return;

    this.isNotificationLoading = true;

    try {
      const response: any = await firstValueFrom(
        this._organizationService.toggleAuthorNotifications(
          this.profile.user_id,
          'experience',
          !this.notificationEnabled
        )
      );

      if (response.status === 'success') {
        this.notificationEnabled = !this.notificationEnabled;
        this._toast.add({
          severity: 'success',
          summary: 'Erfolgreich',
          detail: this.notificationEnabled
            ? 'Benachrichtigungen aktiviert'
            : 'Benachrichtigungen deaktiviert'
        });
      }
    } catch (error) {
      this._toast.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'Fehler beim Aktualisieren'
      });
      console.error('Notification toggle error:', error);
    } finally {
      this.isNotificationLoading = false;
    }
  }

  canFollow(): boolean {
    if (!this.profile) return false;
    const currentUser = this._auth.currentUser;
    return currentUser?.uid !== this.profile.user_id;
  }

  toggleChallengeParticipation(challenge: Challenge) {
    challenge.isParticipating = !challenge.isParticipating;
    challenge.participants += challenge.isParticipating ? 1 : -1;
  }

  getChallengeIcon(category: string): string {
    switch (category) {
      case 'sustainability': return 'pi-leaf';
      case 'community': return 'pi-users';
      case 'health': return 'pi-heart';
      case 'education': return 'pi-book';
      default: return 'pi-star';
    }
  }

  getChallengeColor(category: string): string {
    switch (category) {
      case 'sustainability': return 'text-green-600';
      case 'community': return 'text-blue-600';
      case 'health': return 'text-red-600';
      case 'education': return 'text-purple-600';
      default: return 'text-gray-600';
    }
  }

  getDaysRemaining(endDate: Date): number {
    const now = new Date();
    const diff = endDate.getTime() - now.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  }

  formatTimestamp(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (hours < 1) {
      const minutes = Math.floor(diff / (1000 * 60));
      return `vor ${minutes}m`;
    } else if (hours < 24) {
      return `vor ${hours}h`;
    } else {
      const days = Math.floor(hours / 24);
      return `vor ${days}d`;
    }
  }

  getTopCoinSources(): CoinSource[] {
    return this.coinSources;
  }

  navigateToScanReceipt() {
    this.scannerDialogVisible = true;
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  getLevelTitle(): string {
    const level = this.profile?.level || 1;
    if (level < 5) return 'Anfänger';
    if (level < 10) return 'Fortgeschritten';
    if (level < 20) return 'Experte';
    if (level < 50) return 'Master';
    return 'Legende';
  }

  getRoleBadge(): { label: string; severity: string } {
    switch (this.profile?.user_role) {
      case 'admin':
        return { label: 'Admin', severity: 'danger' };
      case 'moderator':
        return { label: 'Moderator', severity: 'warning' };
      case 'premium':
        return { label: 'Premium', severity: 'info' };
      default:
        return { label: 'Mitglied', severity: 'secondary' };
    }
  }

  async onIonInfinite(event: any) {
    await this.loadPosts(true);
    event.target.complete();
  }

  toSettings() {
    this._nav.navigateForward(['/dashboard/settings']);
  }

  navigateToEnergy() {
    this._nav.navigateForward(['/dashboard/energy']);
  }

  onCommentsUpdated(count: number) {
    if (this.selectedPost) {
      this.selectedPost.comments = count;

      const postIndex = this.posts.findIndex(p => p.id === this.selectedPost!.id);
      if (postIndex > -1) {
        this.posts[postIndex] = {
          ...this.posts[postIndex],
          comments: count
        };
      }
    }
  }

  async openCommentsSheet(postId: number) {
    const post = this.posts.find(p => p.id === postId);
    if (!post) return;

    this.selectedPost = post;
    this.commentsSheetVisible = true;

    try {
      const fullPost = await this._feedService.getPost(post.id);

      if (!fullPost.likes || !Array.isArray(fullPost.likes)) {
        fullPost.likes = [];
      }

      this.selectedPost = fullPost;

      const postIndex = this.posts.findIndex(p => p.id === post.id);
      if (postIndex > -1) {
        this.posts[postIndex] = {
          ...this.posts[postIndex],
          likes: fullPost.likes,
          comments: fullPost.comments
        };
      }
    } catch (error) {
      console.error('Error loading post details:', error);
    }
  }

  closeCommentsSheet() {
    this.commentsSheetVisible = false;

    if (this.selectedPost) {
      const postIndex = this.posts.findIndex(p => p.id === this.selectedPost!.id);
      if (postIndex > -1) {
        this.posts[postIndex] = {
          ...this.posts[postIndex],
          comments: this.selectedPost.comments
        };
      }
    }
  }

  viewAccount(post: FeedPost) {
    if (post.experience_author_id) {
      this._nav.navigateForward(['/dashboard/profile', post.experience_author_id]);
    } else if (post.business_author_id) {
      this._nav.navigateForward(['/dashboard/business-hub', post.business_author_id]);
    } else if (post.regional_author_id) {
      this._nav.navigateForward(['/dashboard/hub', post.regional_author_id]);
    }
  }

  openShareSheet(post: FeedPost) {
    this.postToShare = post;
    this.shareSheetVisible = true;
  }

  closeShareSheet() {
    this.shareSheetVisible = false;
    this.postToShare = null;
  }

  async reportPost(post: FeedPost) {
    try {
      const postIndex = this.posts.findIndex(p => p.id === post.id);
      if (postIndex > -1) {
        this.posts.splice(postIndex, 1);
      }

      await this._reportService.reportPost(post.id, 'inappropriate_content');

      this._messageService.add({
        severity: 'success',
        summary: 'Gemeldet',
        detail: 'Der Post wurde gemeldet und ausgeblendet.'
      });
    } catch (error) {
      await this.loadPosts();

      console.error('Error reporting post:', error);
      this._messageService.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'Meldung konnte nicht gesendet werden.'
      });
    }
  }

  // Transactions Sheet
  openTransactionsSheet() {
    this.transactionsSheetVisible = true;
  }

  closeTransactionsSheet() {
    this.transactionsSheetVisible = false;
  }

  // Donation Methods
  openDonationSheet() {
    this.donationSheetVisible = true;
  }

  closeDonationSheet() {
    this.donationSheetVisible = false;
  }

  async onDonationSuccess() {
    await this.loadDonationStats();
    await this.loadSmartCoinsData();
    await this.loadProfile();
  }

  // Badges Methods
  openBadgesSheet() {
    this.badgesSheetVisible = true;
  }

  closeBadgesSheet() {
    this.badgesSheetVisible = false;
  }

  // Scanner Methods
  closeScannerDialog() {
    this.scannerDialogVisible = false;
    this.resetScanner();
  }

  async startScan() {
    try {
      this.errorMessage = '';
      this.processingStatus = 'Scanne Rechnung...';

      const result = await DocumentScanner.scanDocument({
        croppedImageQuality: 90,
        letUserAdjustCrop: true,
        maxNumDocuments: 1,
        responseType: ResponseType.ImageFilePath,
      });

      // User cancelled - don't show error
      if (result.status !== ScanDocumentResponseStatus.Success) {
        this.resetScanner();
        return;
      }

      if (!result.scannedImages || result.scannedImages.length === 0) {
        this.resetScanner();
        return;
      }

      this.scannerDialogVisible = true;
      this.scannedImage = result.scannedImages[0];
      await this.loadImageForDisplay(result.scannedImages[0]);

      this.isAnalysisInProgress = true;
      await this.processInvoice(result.scannedImages[0]);

    } catch (error: any) {
      console.error('Scan error:', error);
      // Only show error if it's not a cancellation
      if (error.message && !error.message.includes('cancel') && !error.message.includes('Cancel')) {
        this.errorMessage = error.message || 'Fehler beim Scannen';
        this._toast.add({
          severity: 'error',
          summary: 'Scanfehler',
          detail: this.errorMessage
        });
      }
      this.isAnalysisInProgress = false;
    }
  }

  private async loadImageForDisplay(imageUri: string) {
    try {
      if (Capacitor.isNativePlatform()) {
        const readResult = await Filesystem.readFile({
          path: imageUri
        });

        const base64Data = readResult.data as string;
        const extension = imageUri.split('.').pop()?.toLowerCase();
        const mimeType = extension === 'pdf' ? 'application/pdf' : 'image/jpeg';

        this.scannedImageDataUrl = `data:${mimeType};base64,${base64Data}`;
      } else {
        this.scannedImageDataUrl = imageUri;
      }
    } catch (error) {
      console.error('Error loading image for display:', error);
      this.scannedImageDataUrl = imageUri;
    }
  }

  private async processInvoice(imageUri: string) {
    try {
      const user = this._auth.currentUser;
      if (!user) {
        throw new Error('Nicht angemeldet');
      }

      this.processingStatus = 'Bereite Upload vor...';
      const blob = await this.uriToBlob(imageUri);

      this.processingStatus = 'Lade Rechnung hoch...';
      const { tempPath, downloadUrl } = await this._invoiceService.uploadToTempStorage(
        blob,
        user.uid
      );

      this.processingStatus = 'Analysiere Rechnung...';
      const response = await this._invoiceService.processInvoice(tempPath, downloadUrl);

      if (!response.success) {
        await this._invoiceService.cleanupTempFile(tempPath);
        throw new Error(response.error?.message || 'Verarbeitung fehlgeschlagen');
      }

      this.processedInvoice = response.invoice;
      this.processingStatus = 'Erfolgreich verarbeitet!';

      await this.loadProfile();
      await this.loadSmartCoinsData();
      await this.loadBadges(); // Reload badges after invoice processing

      setTimeout(() => {
        this.closeScannerDialog();
      }, 2000);

    } catch (error: any) {
      console.error('Processing error:', error);

      let errorMessage = 'Fehler bei der Verarbeitung';
      let errorCode = 'unknown';

      if (error.message) {
        errorMessage = error.message;
      }

      if (error.code) {
        errorCode = error.code;
      }

      if (error.error) {
        if (error.error.error?.message) {
          errorMessage = error.error.error.message;
          errorCode = error.error.error.code || errorCode;
        } else if (error.error.message) {
          errorMessage = error.error.message;
        }
      }

      this.errorMessage = errorMessage;

      if (errorCode === 'already-exists') {
        this._toast.add({
          severity: 'warn',
          summary: 'Rechnung bereits vorhanden',
          detail: 'Diese Rechnung wurde bereits verarbeitet.'
        });
      } else {
        this._toast.add({
          severity: 'error',
          summary: 'Verarbeitungsfehler',
          detail: errorMessage
        });
      }

      this.isAnalysisInProgress = false;
    }
  }

  private async uriToBlob(uri: string): Promise<Blob> {
    if (Capacitor.isNativePlatform()) {
      const readResult = await Filesystem.readFile({
        path: uri
      });

      const base64Data = readResult.data as string;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const extension = uri.split('.').pop()?.toLowerCase();
      const mimeType = extension === 'pdf' ? 'application/pdf' : 'image/jpeg';

      return new Blob([byteArray], { type: mimeType });
    } else {
      const response = await fetch(uri);
      const blob = await response.blob();
      return blob;
    }
  }

  resetScanner() {
    this.scannedImage = undefined;
    this.scannedImageDataUrl = undefined;
    this.isAnalysisInProgress = false;
    this.processingStatus = '';
    this.processedInvoice = undefined;
    this.errorMessage = '';
  }

  get isAndroid(): boolean {
    return this.platform.is('android');
  }

  viewInvoice(invoice: any) {
    if (invoice.downloadUrl) {
      window.open(invoice.downloadUrl, '_blank');
    }
  }

  viewBusinessHub(hubId: any) {
    this._nav.navigateForward(['/dashboard/business-hub', hubId.toString()]);
  }

  // Pull-to-Refresh Handler
  async handleRefresh(event: any) {
    try {
      await Promise.all([
        this.loadProfile(),
        this.loadPosts(),
        this.loadBadges(),
        !this.isViewMode ? this.loadSmartCoinsData() : Promise.resolve(),
        !this.isViewMode ? this.loadDonationStats() : Promise.resolve(),
        !this.isViewMode && this.profile?.is_energy_customer ? this.loadEnergyData() : Promise.resolve()
      ]);

      this._toast.add({
        severity: 'success',
        summary: 'Aktualisiert',
        detail: 'Daten wurden erfolgreich aktualisiert',
        life: 2000
      });
    } catch (error) {
      console.error('Error refreshing data:', error);
      this._toast.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'Daten konnten nicht aktualisiert werden'
      });
    } finally {
      event.target.complete();
    }
  }
  onPollResultsOpened(post: FeedPost) {
    console.log("hallo");
    
    this.selectedPollPost = post;

    // Check if current user is the poll creator
    this.isPollCreator = (
      post.experience_author_id === this.currentUserId
    );

    this.pollResultsSheetVisible = true;
  }

  closePollResultsSheet() {
    this.pollResultsSheetVisible = false;
    this.selectedPollPost = null;
    this.isPollCreator = false;
  }
}